import React, { useContext, useEffect, memo, useCallback } from 'react';
import { AlertContext, AlertMessage, AlertContextState } from './context';
import { Box, Collapse, Alert, Text, Flex } from '@tonic-ui/react';

interface CloudOneAlertProps
  extends AlertMessage,
    Pick<AlertContextState, 'alertsVisible' | 'setAlertsVisible'> {
  onClose: () => void;
}

const CloudOneAlert = ({
  onClose,
  setAlertsVisible,
  severity,
  text,
  alertsVisible,
}: CloudOneAlertProps): JSX.Element => {
  useEffect(() => {
    let timeoutId;
    // Auto-close success alerts after 5s
    if (severity === 'success')
      timeoutId = setTimeout(() => {
        setAlertsVisible(false);
      }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Collapse in={alertsVisible}>
      <Alert
        severity={severity}
        isClosable
        data-id={`alert-${severity}`}
        onClose={() => setAlertsVisible(false)}
      >
        <Flex justify="space-between" alignItems="flex-start">
          <Text>{text}</Text>
        </Flex>
      </Alert>
    </Collapse>
  );
};

const Alerts = (): JSX.Element => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('<Alerts> must be used within an AlertProvider');
  }

  const { alertMessage, alertsVisible, setAlertMessage, setAlertsVisible } = context;

  const removeAlert = useCallback(() => {
    setAlertMessage(undefined);
  }, [setAlertMessage]);

  return (
    <>
      {alertMessage && (
        // -16px added to offset 16px padding-top from content-section.tsx so alert is flush against header
        <Box marginBottom="4x" marginTop={-16}>
          <CloudOneAlert
            key={alertMessage.id || alertMessage.text}
            onClose={removeAlert}
            alertsVisible={alertsVisible}
            setAlertsVisible={setAlertsVisible}
            {...alertMessage}
          />
        </Box>
      )}
    </>
  );
};

export default memo(Alerts);
